import { useEffect, useState } from "react";
import Modal from "../Modal/Modal";
import Titulo from "../Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { Button } from "../Button";
import { Input } from "../Input";
import { Toggle } from "../Toggle/Toggle";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { tagsApi } from "../../hooks/tagsApi";
import { TagDestaque } from "../../types/Produto";
import { ContainerModalTagsDestaque, CriarTagContainer, Botoes } from "./styles";
import { ReactComponent as Voltar } from "../../assets/newImages/icons/angle-down.svg";
import Swal from "sweetalert2";
import Table from "../Table/Table";

interface ModalTagsDestaqueProps {
  isOpen: boolean;
  setModal: (value: boolean) => void;
  onSave?: (tags: TagDestaque[]) => void;
}

export const ModalTagsDestaque: React.FC<ModalTagsDestaqueProps> = ({
  isOpen,
  setModal,
  onSave,
}) => {
  const { theme } = useTheme();
  const [tags, setTags] = useState<TagDestaque[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSalvar, setLoadingSalvar] = useState(false);
  const [adicionandoTag, setAdicionandoTag] = useState(false);
  const [editandoTag, setEditandoTag] = useState<TagDestaque | null>(null);
  const [tagNome, setTagNome] = useState("");

  const tagsPadrao = ["Lançamento", "Mais vendidos", "Reposição"];

  const fetchTags = async () => {
    setLoading(true);
    try {
      const response = await tagsApi().getTags();
      setTags(response);
    } catch (error) {
      console.error("Erro ao buscar tags:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTags();
      setAdicionandoTag(false);
      setEditandoTag(null);
      setTagNome("");
    }
  }, [isOpen]);

  const handleToggleAtivo = async (tag: TagDestaque) => {
    try {
      const novoAtivo = tag.ativo === 1 ? 0 : 1;
      await tagsApi().atualizarTag(tag.id, { ativo: novoAtivo });
      setTags(tags.map(t =>
        t.id === tag.id ? { ...t, ativo: novoAtivo } : t
      ));
    } catch (error) {
      console.error("Erro ao atualizar tag:", error);
      Swal.fire({
        title: "Erro!",
        text: "Não foi possível atualizar a tag.",
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  const handleSalvarTag = async () => {
    if (tagNome.trim() === "") {
      Swal.fire({
        title: "Erro!",
        text: "O nome da tag não pode estar vazio.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (tagNome.length > 20) {
      Swal.fire({
        title: "Erro!",
        text: "O nome da tag deve ter no máximo 20 caracteres.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    setLoadingSalvar(true);

    try {
      if (editandoTag) {
        await tagsApi().atualizarTag(editandoTag.id, { nome_tag: tagNome.trim() });
        setTags(tags.map(t =>
          t.id === editandoTag.id ? { ...t, nome_tag: tagNome.trim() } : t
        ));
      } else {
        const newTag = await tagsApi().criarTag(tagNome.trim());
        setTags([...tags, newTag]);
      }
      setAdicionandoTag(false);
      setEditandoTag(null);
      setTagNome("");
    } catch (error) {
      console.error("Erro ao salvar tag:", error);
      Swal.fire({
        title: "Erro!",
        text: editandoTag ? "Não foi possível atualizar a tag." : "Não foi possível criar a tag.",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      setLoadingSalvar(false);
    }
  };

  const handleDeleteTag = async (tag: TagDestaque) => {
    if (tagsPadrao.includes(tag.nome_tag)) {
      Swal.fire({
        title: "Erro!",
        text: "Não é possível remover tags padrão.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Remover tag?",
      text: `Deseja remover a tag "${tag.nome_tag}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await tagsApi().removerTag(tag.id);
        setTags(tags.filter(t => t.id !== tag.id));
      } catch (error) {
        console.error("Erro ao remover tag:", error);
        Swal.fire({
          title: "Erro!",
          text: "Não foi possível remover a tag.",
          icon: "error",
          confirmButtonText: "Ok",
        });
      }
    }
  };

  const handleSave = () => {
    onSave?.(tags);
    setModal(false);
  };

  const handleEditarClick = (tag: TagDestaque) => {
    setEditandoTag(tag);
    setTagNome(tag.nome_tag);
    setAdicionandoTag(true);
  };

  const handleNovaTagClick = () => {
    setEditandoTag(null);
    setTagNome("");
    setAdicionandoTag(true);
  };

  if (adicionandoTag) {
    return (
      <Modal isOpen={isOpen} setModal={setModal} width="500px" maxWidth="90vw">
        <CriarTagContainer theme={theme}>
          <div className="titulo fonteTitulo">
            {editandoTag ? "Editar tag" : "Nova tag"}
          </div>
          <div className="texto fonteRotulos">
            {editandoTag
              ? "Edite o nome da tag de destaque"
              : "Adicione um nome para identificar sua tag de destaque"
            }
          </div>
          <div className="inputs">
            <Input
              label="Nome da tag"
              type="text"
              maxLength={20}
              name="nome"
              value={tagNome}
              onChange={(newValue) => setTagNome(newValue)}
              placeholder="Nome da tag"
            />
          </div>
          {editandoTag && !tagsPadrao.includes(editandoTag.nome_tag) && (
            <div
              className="remover-tag fonteTexto"
              onClick={() => {
                handleDeleteTag(editandoTag);
                setAdicionandoTag(false);
                setEditandoTag(null);
              }}
            >
              Remover tag
            </div>
          )}
          <Botoes theme={theme}>
            <div
              className="cancelar fonteTexto"
              onClick={() => {
                setAdicionandoTag(false);
                setEditandoTag(null);
                setTagNome("");
              }}
            >
              <Voltar /> Voltar
            </div>
            <Button
              title="Salvar"
              loading={loadingSalvar}
              className="botao_alterar fonteTexto"
              width="25rem"
              onClick={handleSalvarTag}
              disabled={tagNome.length === 0}
            />
          </Botoes>
        </CriarTagContainer>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} setModal={setModal} width="600px">
      <ContainerModalTagsDestaque theme={theme}>
        <Titulo
          titulo="Tag de destaque"
          subtitulo="Configure as tags de destaque que deseja apresentar em seus produtos"
          flexDirection="column-reverse"
        />

        {loading ? (
          <div className="loading fonteTexto">Carregando tags...</div>
        ) : (
          <>
            {tags.length > 0 && (
              <Table
                columns={[
                  {
                    key: "nome_tag",
                    label: "Nome",
                    mask: (value: string) => (
                      <span
                        className="fonteTexto nome-tag"
                        onClick={() => {
                          const tag = tags.find(t => t.nome_tag === value);
                          if (tag) handleEditarClick(tag);
                        }}
                      >
                        {value}
                      </span>
                    )
                  },
                  {
                    key: "situacao",
                    label: "Situação",
                    mask: (value: any) => value
                  }
                ]}
                data={tags.map((tag) => ({
                  ...tag,
                  situacao: (
                    <div className="center">
                      <Toggle
                        value={tag.ativo === 1}
                        onChange={() => handleToggleAtivo(tag)}
                      />
                      <span
                        onClick={() => handleEditarClick(tag)}
                        className="editar fonteTexto"
                      >
                        Editar
                      </span>
                    </div>
                  )
                }))}
                sortable={false}
                selectable={false}
                button={false}
                page={1}
                itensPorPagina={-1}
              />
            )}

            <div className="linha-pai">
              <div className="linha"></div>
            </div>
            <div className="nova-tag fonteTexto">
              <span onClick={handleNovaTagClick}>
                Adicionar nova tag
              </span>
            </div>
          </>
        )}

        <Botoes theme={theme}>
          <div className="cancelar fonteTexto" onClick={() => setModal(false)}>
            <Voltar /> Voltar
          </div>
          <Button
            title="Salvar"
            onClick={handleSave}
            width="50%"
            className={`fonteTexto ${loading && "disabled"}`}
          />
        </Botoes>
      </ContainerModalTagsDestaque>
    </Modal>
  );
};
